import React, { useState } from 'react';
import kycTestData from '../utils/kycTestData';
import './KYCValidationTester.css';

const KYCValidationTester = () => {
  const [selectedScenario, setSelectedScenario] = useState('perfectMatch');
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [testProgress, setTestProgress] = useState(0);

  const testScenarios = kycTestData.getTestScenarios();
  const faceMatchData = kycTestData.getFaceMatchTestData();
  const consistencyPatterns = kycTestData.getDataConsistencyPatterns();
  const authenticityPatterns = kycTestData.getAuthenticityTestPatterns();
  const statusTransitions = kycTestData.getStatusTransitionTests();

  const runValidationTest = async () => {
    setIsRunning(true);
    setTestProgress(0);
    setTestResults(null);

    try {
      const scenario = testScenarios[selectedScenario];
      
      // Simulate comprehensive validation process
      const results = {
        scenario: selectedScenario,
        timestamp: new Date().toISOString(),
        testSteps: {},
        finalScore: 0,
        status: 'PENDING',
        recommendations: []
      };

      // Step 1: Document Authenticity Test
      setTestProgress(20);
      await new Promise(resolve => setTimeout(resolve, 1000));
      results.testSteps.authenticity = await testDocumentAuthenticity(scenario);

      // Step 2: Document Expiry Test
      setTestProgress(40);
      await new Promise(resolve => setTimeout(resolve, 800));
      results.testSteps.expiry = await testDocumentExpiry(scenario);

      // Step 3: Face Match Test
      setTestProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1500));
      results.testSteps.faceMatch = await testFaceMatch(scenario);

      // Step 4: Data Consistency Test
      setTestProgress(80);
      await new Promise(resolve => setTimeout(resolve, 1200));
      results.testSteps.dataConsistency = await testDataConsistency(scenario);

      // Step 5: Calculate Final Results
      setTestProgress(95);
      await new Promise(resolve => setTimeout(resolve, 500));
      results.finalScore = calculateFinalScore(results.testSteps);
      results.status = determineStatus(results.finalScore, results.testSteps);
      results.recommendations = generateRecommendations(results.testSteps);
      results.statusTransition = getStatusTransition(results.status, results.finalScore);

      setTestProgress(100);
      setTestResults(results);

    } catch (error) {
      console.error('Test execution failed:', error);
      setTestResults({
        error: 'Test execution failed',
        message: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const testDocumentAuthenticity = async (scenario) => {
    const identityAuth = scenario.identity?.authenticity || authenticityPatterns.authentic;
    const addressAuth = scenario.address?.authenticity || authenticityPatterns.authentic;

    return {
      identity: {
        confidence: identityAuth.confidence,
        isAuthentic: identityAuth.confidence > 0.7,
        tamperingDetected: identityAuth.tamperingDetected,
        qualityScore: identityAuth.qualityScore,
        securityFeatures: identityAuth.securityFeatures,
        passed: identityAuth.confidence > 0.7 && !identityAuth.tamperingDetected
      },
      address: {
        confidence: addressAuth.confidence,
        isAuthentic: addressAuth.confidence > 0.7,
        tamperingDetected: addressAuth.tamperingDetected,
        qualityScore: addressAuth.qualityScore,
        securityFeatures: addressAuth.securityFeatures,
        passed: addressAuth.confidence > 0.7 && !addressAuth.tamperingDetected
      },
      overallScore: Math.round(((identityAuth.confidence + addressAuth.confidence) / 2) * 100)
    };
  };

  const testDocumentExpiry = async (scenario) => {
    const identityExpiry = scenario.identity?.expiry || { isValid: true, isPermanent: true };
    const addressExpiry = scenario.address?.expiry || { isValid: true, isPermanent: true };

    return {
      identity: {
        isValid: identityExpiry.isValid,
        isPermanent: identityExpiry.isPermanent,
        isExpired: identityExpiry.isExpired || false,
        expiryDate: identityExpiry.expiryDate,
        passed: identityExpiry.isValid && !identityExpiry.isExpired
      },
      address: {
        isValid: addressExpiry.isValid,
        isPermanent: addressExpiry.isPermanent,
        isExpired: addressExpiry.isExpired || false,
        expiryDate: addressExpiry.expiryDate,
        passed: addressExpiry.isValid && !addressExpiry.isExpired
      },
      overallScore: (identityExpiry.isValid && addressExpiry.isValid) ? 100 : 0
    };
  };

  const testFaceMatch = async (scenario) => {
    let faceMatchResult;
    
    // Determine face match quality based on scenario
    if (selectedScenario === 'perfectMatch') {
      faceMatchResult = faceMatchData.highMatch;
    } else if (selectedScenario === 'goodMatchWithIssues') {
      faceMatchResult = faceMatchData.mediumMatch;
    } else {
      faceMatchResult = faceMatchData.lowMatch;
    }

    return {
      identityToAddress: {
        matchScore: faceMatchResult.matchScore,
        isMatch: faceMatchResult.isMatch,
        confidence: faceMatchResult.confidence,
        qualityChecks: faceMatchResult.qualityChecks,
        passed: faceMatchResult.isMatch && faceMatchResult.confidence > 0.7
      },
      identityToSelfie: {
        matchScore: faceMatchResult.matchScore * 0.98, // Slightly different for selfie
        isMatch: faceMatchResult.isMatch,
        confidence: faceMatchResult.confidence * 0.95,
        livenessScore: faceMatchResult.livenessScore,
        spoofingDetected: faceMatchResult.spoofingDetected,
        passed: faceMatchResult.isMatch && faceMatchResult.livenessScore > 0.6
      },
      overallScore: Math.round(faceMatchResult.confidence * 100)
    };
  };

  const testDataConsistency = async (scenario) => {
    let consistencyPattern;
    
    // Determine consistency based on scenario
    if (selectedScenario === 'dataInconsistency') {
      consistencyPattern = consistencyPatterns.majorInconsistency;
    } else if (selectedScenario === 'goodMatchWithIssues') {
      consistencyPattern = consistencyPatterns.minorInconsistency;
    } else {
      consistencyPattern = consistencyPatterns.perfectConsistency;
    }

    const nameConsistency = consistencyPattern.nameMatch?.consistency || 1.0;
    const addressConsistency = consistencyPattern.addressMatch?.consistency || 1.0;
    const dobConsistency = consistencyPattern.dobMatch?.consistency || 1.0;

    return {
      nameConsistency: {
        score: nameConsistency,
        isConsistent: nameConsistency > 0.8,
        variations: consistencyPattern.nameMatch?.variations || ['John Doe'],
        passed: nameConsistency > 0.8
      },
      addressConsistency: {
        score: addressConsistency,
        isConsistent: addressConsistency > 0.8,
        matchingFields: ['street', 'city', 'pincode'],
        passed: addressConsistency > 0.8
      },
      dobConsistency: {
        score: dobConsistency,
        isConsistent: dobConsistency > 0.9,
        passed: dobConsistency > 0.9
      },
      overallScore: Math.round(((nameConsistency + addressConsistency + dobConsistency) / 3) * 100)
    };
  };

  const calculateFinalScore = (testSteps) => {
    const weights = {
      authenticity: 0.3,
      expiry: 0.2,
      faceMatch: 0.25,
      dataConsistency: 0.25
    };

    let totalScore = 0;
    Object.keys(weights).forEach(step => {
      if (testSteps[step]?.overallScore) {
        totalScore += testSteps[step].overallScore * weights[step];
      }
    });

    return Math.round(totalScore);
  };

  const determineStatus = (finalScore, testSteps) => {
    // Check for critical failures
    const criticalFailures = [];
    
    if (!testSteps.authenticity?.identity?.passed) {
      criticalFailures.push('Identity document authenticity failed');
    }
    if (!testSteps.expiry?.identity?.passed) {
      criticalFailures.push('Identity document expired');
    }
    if (!testSteps.faceMatch?.identityToSelfie?.passed) {
      criticalFailures.push('Face match verification failed');
    }
    if (!testSteps.dataConsistency?.nameConsistency?.passed) {
      criticalFailures.push('Name consistency check failed');
    }

    if (criticalFailures.length > 0) {
      return 'REJECTED';
    }

    if (finalScore >= 75) {  // Lowered from 85 to 75
      return 'APPROVED';
    } else if (finalScore >= 60) {  // Lowered from 70 to 60
      return 'MANUAL_REVIEW';
    } else {
      return 'REJECTED';
    }
  };

  const generateRecommendations = (testSteps) => {
    const recommendations = [];

    if (!testSteps.authenticity?.identity?.passed) {
      recommendations.push('Identity document quality is insufficient - request new upload');
    }
    if (!testSteps.authenticity?.address?.passed) {
      recommendations.push('Address document appears tampered - manual verification required');
    }
    if (!testSteps.faceMatch?.identityToSelfie?.passed) {
      recommendations.push('Face match confidence is low - consider additional verification methods');
    }
    if (!testSteps.dataConsistency?.nameConsistency?.passed) {
      recommendations.push('Name inconsistencies detected - verify with additional documents');
    }

    return recommendations;
  };

  const getStatusTransition = (status, score) => {
    const transitions = statusTransitions;
    
    switch (status) {
      case 'APPROVED':
        return {
          ...transitions.pendingToApproved,
          score,
          reason: `Auto-approved with confidence score of ${score}%`
        };
      case 'MANUAL_REVIEW':
        return {
          ...transitions.pendingToManualReview,
          score,
          reason: `Manual review required due to confidence score of ${score}%`
        };
      case 'REJECTED':
        return {
          ...transitions.pendingToRejected,
          score,
          reason: `Rejected due to low confidence score of ${score}%`
        };
      default:
        return transitions.pendingToApproved;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return '#28a745';
      case 'MANUAL_REVIEW': return '#ffc107';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getScenarioDescription = (scenarioKey) => {
    const descriptions = {
      perfectMatch: 'All documents are authentic, high-quality, and data is consistent. Expected: APPROVED',
      goodMatchWithIssues: 'Good quality documents with minor issues. Expected: MANUAL_REVIEW',
      poorQuality: 'Poor quality or tampered documents. Expected: REJECTED',
      dataInconsistency: 'Data inconsistencies between documents. Expected: REJECTED',
      expiredDocuments: 'Documents are expired or invalid. Expected: REJECTED'
    };
    return descriptions[scenarioKey] || 'Test scenario';
  };

  return (
    <div className="kyc-validation-tester">
      <div className="tester-header">
        <h2>🧪 KYC Validation Test Suite</h2>
        <p>Test comprehensive KYC validation with different document scenarios</p>
      </div>

      {/* Scenario Selection */}
      <div className="scenario-selection">
        <h3>📋 Test Scenarios</h3>
        <div className="scenario-grid">
          {Object.keys(testScenarios).map(scenarioKey => (
            <div 
              key={scenarioKey}
              className={`scenario-card ${selectedScenario === scenarioKey ? 'selected' : ''}`}
              onClick={() => setSelectedScenario(scenarioKey)}
            >
              <h4>{scenarioKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
              <p>{getScenarioDescription(scenarioKey)}</p>
              <div className="expected-outcome">
                Expected: <span className={`outcome ${testScenarios[scenarioKey].expectedOutcome.toLowerCase()}`}>
                  {testScenarios[scenarioKey].expectedOutcome}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Controls */}
      <div className="test-controls">
        <button 
          className="btn btn-primary btn-lg"
          onClick={runValidationTest}
          disabled={isRunning}
        >
          {isRunning ? '🔄 Running Test...' : '🚀 Run Validation Test'}
        </button>
      </div>

      {/* Test Progress */}
      {isRunning && (
        <div className="test-progress">
          <h4>Test Progress</h4>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${testProgress}%` }}
            ></div>
          </div>
          <p>{testProgress}% Complete</p>
        </div>
      )}

      {/* Test Results */}
      {testResults && !testResults.error && (
        <div className="test-results">
          <div className="results-header">
            <h3>📊 Test Results</h3>
            <div className="final-score">
              <span>Final Score: </span>
              <span className="score-value">{testResults.finalScore}%</span>
            </div>
          </div>

          <div className="status-result">
            <h4 style={{ color: getStatusColor(testResults.status) }}>
              Status: {testResults.status}
            </h4>
            <p>{testResults.statusTransition.reason}</p>
          </div>

          {/* Detailed Step Results */}
          <div className="step-results">
            <h4>🔍 Detailed Results</h4>
            
            {/* Authenticity Results */}
            <div className="step-result-card">
              <h5>Document Authenticity ({testResults.testSteps.authenticity?.overallScore}%)</h5>
              <div className="result-details">
                <div className="result-item">
                  <span>Identity Document:</span>
                  <span className={testResults.testSteps.authenticity?.identity?.passed ? 'passed' : 'failed'}>
                    {testResults.testSteps.authenticity?.identity?.confidence * 100}% confidence
                    {testResults.testSteps.authenticity?.identity?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="result-item">
                  <span>Address Document:</span>
                  <span className={testResults.testSteps.authenticity?.address?.passed ? 'passed' : 'failed'}>
                    {testResults.testSteps.authenticity?.address?.confidence * 100}% confidence
                    {testResults.testSteps.authenticity?.address?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
              </div>
            </div>

            {/* Face Match Results */}
            <div className="step-result-card">
              <h5>Face Match Verification ({testResults.testSteps.faceMatch?.overallScore}%)</h5>
              <div className="result-details">
                <div className="result-item">
                  <span>Identity-Address Match:</span>
                  <span className={testResults.testSteps.faceMatch?.identityToAddress?.passed ? 'passed' : 'failed'}>
                    {(testResults.testSteps.faceMatch?.identityToAddress?.confidence * 100).toFixed(1)}%
                    {testResults.testSteps.faceMatch?.identityToAddress?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="result-item">
                  <span>Identity-Selfie Match:</span>
                  <span className={testResults.testSteps.faceMatch?.identityToSelfie?.passed ? 'passed' : 'failed'}>
                    {(testResults.testSteps.faceMatch?.identityToSelfie?.confidence * 100).toFixed(1)}%
                    {testResults.testSteps.faceMatch?.identityToSelfie?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="result-item">
                  <span>Liveness Score:</span>
                  <span>{(testResults.testSteps.faceMatch?.identityToSelfie?.livenessScore * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Data Consistency Results */}
            <div className="step-result-card">
              <h5>Data Consistency ({testResults.testSteps.dataConsistency?.overallScore}%)</h5>
              <div className="result-details">
                <div className="result-item">
                  <span>Name Consistency:</span>
                  <span className={testResults.testSteps.dataConsistency?.nameConsistency?.passed ? 'passed' : 'failed'}>
                    {(testResults.testSteps.dataConsistency?.nameConsistency?.score * 100).toFixed(1)}%
                    {testResults.testSteps.dataConsistency?.nameConsistency?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="result-item">
                  <span>Address Consistency:</span>
                  <span className={testResults.testSteps.dataConsistency?.addressConsistency?.passed ? 'passed' : 'failed'}>
                    {(testResults.testSteps.dataConsistency?.addressConsistency?.score * 100).toFixed(1)}%
                    {testResults.testSteps.dataConsistency?.addressConsistency?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
              </div>
            </div>

            {/* Document Expiry Results */}
            <div className="step-result-card">
              <h5>Document Expiry ({testResults.testSteps.expiry?.overallScore}%)</h5>
              <div className="result-details">
                <div className="result-item">
                  <span>Identity Document:</span>
                  <span className={testResults.testSteps.expiry?.identity?.passed ? 'passed' : 'failed'}>
                    {testResults.testSteps.expiry?.identity?.isValid ? 'Valid' : 'Invalid'}
                    {testResults.testSteps.expiry?.identity?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="result-item">
                  <span>Address Document:</span>
                  <span className={testResults.testSteps.expiry?.address?.passed ? 'passed' : 'failed'}>
                    {testResults.testSteps.expiry?.address?.isValid ? 'Valid' : 'Invalid'}
                    {testResults.testSteps.expiry?.address?.passed ? ' ✅' : ' ❌'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          {testResults.recommendations.length > 0 && (
            <div className="recommendations">
              <h4>💡 Recommendations</h4>
              <ul>
                {testResults.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {testResults?.error && (
        <div className="test-error">
          <h4>❌ Test Error</h4>
          <p>{testResults.message}</p>
        </div>
      )}
    </div>
  );
};

export default KYCValidationTester;